import {
  getAccountHashrateChart,
  getAccountHashrateHistory,
  getAccountOverview,
  getRewardHistory,
  getUserInfo,
  getWorkerHashrate,
  getWorkerHashrateHistory,
  getWorkerOverview,
  getWorkers,
  getWorkersStats,
  updateEmail,
  updateUserSecretKey as _updateUserSecretKey
} from '@/api/user'
import {
  QUERY_CONFIGS,
  createQueryKey,
  createReactiveParams,
  createSmartRetry,
  useQueryPerformance
} from '@/lib/query-helpers'
import { extractData, queryClient } from '@/lib/vue-query'
import { useAuthStore } from '@/stores/authStore'
import { useCurrencyStore } from '@/stores/currencyStore'
import type {
  AccountOverview,
  HashrateChart,
  HashrateHistory,
  RewardHistory,
  WorkerHashrateHistory,
  WorkerOverview,
  WorkersHashrate,
  WorkersStats
} from '@/types/api'
import type { MaybeReactive } from '@/utils/reactive-helpers'
import { useMutation, useQuery } from '@tanstack/vue-query'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

/**
 * User-related API queries
 *


 */

// Smart retry configuration for user operations
const userRetry = createSmartRetry(2, [2000, 5000, 5001, 5002])

/**
 * User info query
 */
export const useUserInfoQuery = () => {
  const authStore = useAuthStore()
  const queryKey = createQueryKey.user.info()
  const performance = useQueryPerformance(queryKey)

  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await getUserInfo()
      const data = extractData(response, 'UserInfoQuery', 'getUserInfo')
      performance.trackDuration()
      return data
    },
    enabled: computed(() => authStore.sessionReady && authStore.isAuthenticated),
    ...QUERY_CONFIGS.FREQUENT,
    retry: userRetry
  })
}

/**
 * Update user email mutation
 */
export const useUpdateEmailMutation = () => {
  return useMutation({
    mutationFn: async (params: { email: string; confirmation_code: number }) => {
      const response = await updateEmail(params)
      return extractData(response, 'UpdateEmailMutation', 'updateEmail')
    },
    onSuccess: () => {
      // Invalidate user info to show new email
      queryClient.invalidateQueries({ queryKey: createQueryKey.user.info() })
    },
    retry: userRetry
  })
}

/**
 * Account overview query
 */
export const useAccountOverviewQuery = (coin?: string, options?: { enabled?: ComputedRef<boolean> }) => {
  const authStore = useAuthStore()
  const currencyStore = useCurrencyStore()

  // Use provided coin or fall back to centralized currency store
  const effectiveCoin = computed(() => coin || currencyStore.selectedCurrency)

  const reactiveParams = createReactiveParams({ coin: effectiveCoin })

  return useQuery({
    queryKey: computed(() => createQueryKey.user.overview(reactiveParams.value.coin)),
    queryFn: async () => {
      const response = await getAccountOverview(reactiveParams.value.coin ? { coin: reactiveParams.value.coin } : undefined)
      return extractData(response, 'AccountOverviewQuery', 'getAccountOverview') as AccountOverview
    },
    enabled: computed(() => {
      const baseEnabled = authStore.sessionReady && authStore.isAuthenticated
      return options?.enabled?.value !== undefined ? baseEnabled && options.enabled.value : baseEnabled
    }),
    ...QUERY_CONFIGS.FREQUENT,
    retry: userRetry
  })
}

/**
 * Workers list query
 */
export const useWorkersQuery = (coin?: string) => {
  const authStore = useAuthStore()
  const reactiveParams = createReactiveParams({ coin })

  return useQuery({
    queryKey: computed(() => createQueryKey.user.workers(reactiveParams.value.coin)),
    queryFn: async () => {
      const response = await getWorkers(reactiveParams.value.coin ? { coin: reactiveParams.value.coin } : undefined)
      return extractData(response, 'WorkersQuery', 'getWorkers')
    },
    enabled: computed(() => authStore.sessionReady && authStore.isAuthenticated),
    ...QUERY_CONFIGS.FREQUENT,
    retry: userRetry
  })
}

/**
 * Reward history query
 */
export const useRewardHistoryQuery = (params?: {
  start_date?: MaybeReactive<string | undefined>
  end_date?: MaybeReactive<string | undefined>
  range?: MaybeReactive<'week' | 'month' | 'year' | undefined>
  page?: MaybeReactive<number>
  limit?: MaybeReactive<number>
  coin?: MaybeReactive<string>
  sort_field?: MaybeReactive<string>
  sort_order?: MaybeReactive<string>
  worker?: MaybeReactive<string | undefined>
}) => {
  const reactiveParams = createReactiveParams({
    start_date: params?.start_date,
    end_date: params?.end_date,
    range: params?.range,
    coin: params?.coin,
    sort_field: params?.sort_field,
    sort_order: params?.sort_order,
    worker: params?.worker,
    page: params?.page,
    limit: params?.limit
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.user.rewards({
        ...reactiveParams.value
      })
    ),
    queryFn: async () => {
      const response = await getRewardHistory({
        start_date: reactiveParams.value.start_date,
        end_date: reactiveParams.value.end_date,
        range: reactiveParams.value.range,
        page: reactiveParams.value.page,
        limit: reactiveParams.value.limit,
        coin: reactiveParams.value.coin,
        sort_field: reactiveParams.value.sort_field,
        sort_order: reactiveParams.value.sort_order,
        worker: reactiveParams.value.worker
      })
      const data = extractData(response, 'RewardHistoryQuery', 'getRewardHistory')
      return data as RewardHistory
    },
    ...QUERY_CONFIGS.FREQUENT,
    retry: userRetry
  })
}

/**
 * Worker hashrate query
 */
export const useAuthWorkerHashrateQuery = (params?: {
  page?: number
  limit?: number
  coin?: MaybeReactive<string>
  sort_field?: MaybeReactive<string>
  sort_order?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams({
    coin: params?.coin,
    sort_field: params?.sort_field,
    sort_order: params?.sort_order
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.user.hashrate({
        ...reactiveParams.value,
        page: params?.page,
        limit: params?.limit
      })
    ),
    queryFn: async () => {
      const response = await getWorkerHashrate({
        page: params?.page,
        limit: params?.limit,
        coin: reactiveParams.value.coin,
        sort_field: reactiveParams.value.sort_field,
        sort_order: reactiveParams.value.sort_order
      })
      const data = extractData(response, 'WorkerHashrateQuery', 'getWorkerHashrate')
      return data as WorkersHashrate
    },
    ...QUERY_CONFIGS.REAL_TIME,
    retry: userRetry
  })
}

/**
 * Account hashrate history query
 */
export const useAccountHashrateHistoryQuery = (params?: {
  start_date?: string
  end_date?: string
  page?: number
  limit?: number
  coin?: MaybeReactive<string>
  sort_field?: string
  sort_order?: string
}) => {
  const reactiveParams = createReactiveParams({ coin: params?.coin })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.user.hashrateHistory({
        ...reactiveParams.value,
        start_date: params?.start_date,
        end_date: params?.end_date,
        page: params?.page,
        limit: params?.limit,
        sort_field: params?.sort_field,
        sort_order: params?.sort_order
      })
    ),
    queryFn: async () => {
      const response = await getAccountHashrateHistory({
        start_date: params?.start_date,
        end_date: params?.end_date,
        page: params?.page,
        limit: params?.limit,
        coin: reactiveParams.value.coin,
        sort_field: params?.sort_field,
        sort_order: params?.sort_order
      })
      const data = extractData(response, 'AccountHashrateHistoryQuery', 'getAccountHashrateHistory')
      return data as HashrateHistory
    },
    ...QUERY_CONFIGS.HISTORICAL,
    retry: userRetry
  })
}

/**
 * Account hashrate chart query
 * chart_type: 1 (hourly), 2 (daily), 3 (hourly 7 days), 4 (daily month), 5 (daily year)
 */
export const useAccountHashrateChartQuery = (
  params: {
    chart_type: MaybeReactive<1 | 2 | 3 | 4 | 5>
    period?: number
    coin?: MaybeReactive<string>
  },
  options?: { enabled?: ComputedRef<boolean> }
) => {
  const reactiveParams = createReactiveParams({
    chart_type: params.chart_type,
    coin: params.coin
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.user.hashrateChart({
        ...reactiveParams.value,
        period: params.period
      })
    ),
    queryFn: async () => {
      const response = await getAccountHashrateChart({
        chart_type: reactiveParams.value.chart_type,
        period: params.period,
        coin: reactiveParams.value.coin
      })
      const data = extractData(response, 'AccountHashrateChartQuery', 'getAccountHashrateChart')
      return data as HashrateChart
    },
    enabled: options?.enabled,
    ...QUERY_CONFIGS.FREQUENT,
    retry: userRetry
  })
}

/**
 * Worker overview query
 */
export const useAuthWorkerOverviewQuery = (params: {
  worker: MaybeReactive<string>
  coin?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams(params)

  return useQuery({
    queryKey: computed(() => createQueryKey.user.workerOverview(reactiveParams.value)),
    queryFn: async () => {
      if (!reactiveParams.value.worker) {
        throw new Error('Worker name is required')
      }

      const response = await getWorkerOverview(reactiveParams.value)
      const data = extractData(response, 'WorkerOverviewQuery', 'getWorkerOverview')
      return data as WorkerOverview
    },
    enabled: computed(() => !!reactiveParams.value.worker),
    ...QUERY_CONFIGS.REAL_TIME,
    retry: userRetry
  })
}

/**
 * Worker hashrate history query
 */
export const useWorkerHashrateHistoryQuery = (params: {
  worker: MaybeReactive<string>
  chart_type: MaybeReactive<1 | 2 | 3 | 4 | 5>
  period?: MaybeReactive<number>
  coin?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams({
    worker: params.worker,
    chart_type: params.chart_type,
    period: params.period,
    coin: params.coin
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.user.workerHashrateHistory({
        ...reactiveParams.value
      })
    ),
    queryFn: async () => {
      if (!reactiveParams.value.worker) {
        throw new Error('Worker name is required')
      }

      const response = await getWorkerHashrateHistory({
        worker: reactiveParams.value.worker,
        chart_type: reactiveParams.value.chart_type,
        period: reactiveParams.value.period,
        coin: reactiveParams.value.coin
      })
      const data = extractData(response, 'WorkerHashrateHistoryQuery', 'getWorkerHashrateHistory')
      return data as WorkerHashrateHistory
    },
    enabled: computed(() => !!reactiveParams.value.worker),
    ...QUERY_CONFIGS.HISTORICAL,
    retry: userRetry
  })
}

/**
 * Workers stats query
 */
export const useWorkersStatsQuery = (params?: MaybeReactive<{
  page?: number
  limit?: number
  coin?: string
  status?: string
  search?: string
  sort_field?: string
  sort_order?: string
}>) => {
  const reactiveParams = computed(() => {
    if (!params) return {}

    return typeof params === 'object' && 'value' in params ? params.value : params
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.user.workerStats(reactiveParams.value)
    ),
    queryFn: async () => {
      const paramValues = reactiveParams.value
      const response = await getWorkersStats({
        page: paramValues.page,
        limit: paramValues.limit,
        coin: paramValues.coin,
        status: paramValues.status,
        search: paramValues.search,
        sort_field: paramValues.sort_field,
        sort_order: paramValues.sort_order
      })
      const data = extractData(response, 'WorkersStatsQuery', 'getWorkersStats')
      return data as WorkersStats
    },
    ...QUERY_CONFIGS.REAL_TIME,
    retry: userRetry
  })
}
